//
// Runtime-ul JSX al ecranelor de structură: orice text scris într-un element
// DOM trece prin `<TextSimboluri>`, deci orice simbol de formulă (f_cd, ν_d,
// ΣM_Rc, d_r^ULS) primește hover cu explicația din registrul serverului.
//
// Fișierele care îl folosesc au pe prima linie pragma de import JSX cu sursa
// `./simboluri-jsx` (v. `tests/test_simboluri.py`, care o cere) și nimic
// altceva nu se schimbă în ele. Explicația vine dintr-un singur loc
// (`structure/simboluri.py`), iar acoperirea e completă prin construcție: un
// text nou cu un simbol nou nu poate rămâne fără hover — cel mult fără intrare
// în registru, ceea ce prinde `tests/test_simboluri.py`.
//
// Ce NU se atinge: copiii care nu sunt șiruri (elemente, expresii), textele
// fără nimic care să arate ca un simbol (verificare ieftină, cu un regex), și
// elementele în care un `<span>` ar fi invalid sau nedorit (`option`,
// `textarea`, textul din SVG). Fișierele care implementează hoverul (Simbol,
// HoverCard, Marime, Info) folosesc runtime-ul standard — altfel textul
// simbolului din propriul cartonaș s-ar adnota la nesfârșit.
//
// ÎNAINTE de adnotare, textul trece prin `traduceRezultat`: ce au scris pașii
// în bază, în română (numele verificărilor, punctul, nota, referința), iese
// în limba ecranului (v. `../texteRezultat.ts`). Tot aici se traduc `title`
// și `data-tip`, unde ecranele pun aceleași texte la hover. Pe română nu e
// niciun dicționar activ, deci nimic nu se atinge.
import * as R from "react/jsx-runtime";
import type { ReactNode } from "react";
import TextSimboluri from "../Simbol";
import { traduceRezultat } from "../texteRezultat";

export { Fragment } from "react/jsx-runtime";
export type { JSX } from "react/jsx-runtime";

const FARA_ADNOTARE = new Set([
  "option", "textarea", "title", "script", "style", "select",
  "text", "tspan", "textPath", "desc",
]);

// ceva care ar putea fi simbol: indice/exponent după o literă, literă
// grecească, indice tipografic, sumă, radical. Filtrul fin (ce e în registru)
// îl face TextSimboluri.
const POATE_AVEA_SIMBOL = /[A-Za-zΑ-Ωα-ω][_^,][A-Za-z0-9ăâîșț']|[Ͱ-Ͽ₀-ₜ√Σ]/;

// elementele al căror text nu e scris de un pas: codul, stilul, ce tastează omul
const FARA_TRADUCERE = new Set(["script", "style", "textarea"]);
// atributele cu text de citit, pe care ecranele pun textele pașilor la hover
const ATRIBUTE_TRADUSE = ["title", "data-tip"] as const;

function adnoteaza(copil: ReactNode, cuSimboluri: boolean): ReactNode {
  if (typeof copil === "string") {
    const text = traduceRezultat(copil);
    return cuSimboluri && POATE_AVEA_SIMBOL.test(text)
      ? R.jsx(TextSimboluri, { text })
      : text;
  }
  if (Array.isArray(copil)) {
    let schimbat = false;
    const nou = copil.map((c) => {
      const a = adnoteaza(c, cuSimboluri);
      if (a !== c) schimbat = true;
      return a;
    });
    return schimbat ? nou : copil;
  }
  return copil;
}

function cuTextAdnotat(type: unknown, props: Record<string, unknown> | null) {
  if (typeof type !== "string" || FARA_TRADUCERE.has(type) || !props) {
    return props;
  }
  let p = props;
  for (const a of ATRIBUTE_TRADUSE) {
    const v = p[a];
    if (typeof v === "string") {
      const tradus = traduceRezultat(v);
      if (tradus !== v) p = { ...p, [a]: tradus };
    }
  }
  if (!("children" in p)) return p;
  const copii = adnoteaza(p.children as ReactNode, !FARA_ADNOTARE.has(type));
  return copii === p.children ? p : { ...p, children: copii };
}

type Jsx = typeof R.jsx;

export const jsx: Jsx = (type, props, key) =>
  R.jsx(type as never, cuTextAdnotat(type, props as never) as never, key);

export const jsxs: Jsx = (type, props, key) =>
  R.jsxs(type as never, cuTextAdnotat(type, props as never) as never, key);
